import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/api';
import LandscapePipelinePanel from './LandscapePipelinePanel';

type CatalogItem = {
  id: number;
  slug: string;
  title: string;
  disease_area?: string | null;
  line_of_therapy?: string | null;
  version_label?: string | null;
  version_number?: number;
  is_published?: boolean;
  created_at?: string | null;
  indication_name?: string | null;
  column_count?: number;
};

type IndicationOption = { id: string; name: string };

function formatUploadedAt(value?: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function uniqueSorted(values: Array<string | null | undefined>): string[] {
  return Array.from(
    new Set(values.map((v) => (v || '').trim()).filter(Boolean)),
  ).sort((a, b) => a.localeCompare(b));
}

/** Lightweight CSV peek for upload autofill (title / line suggestions). */
async function peekLandscapeCsvHints(file: File): Promise<{
  title?: string;
  suggestedLine?: string;
  suggestedSlug?: string;
}> {
  const text = await file.text();
  const lines = text.split(/\r?\n/).slice(0, 6);
  const cells = (line: string) => {
    const out: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        out.push(cur);
        cur = '';
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.map((c) => c.trim());
  };
  const rows = lines.map(cells).filter((r) => r.some((c) => c));
  if (!rows.length) return {};
  const title = (rows[0][0] || '').replace(/\n/g, ' — ').trim();
  const subtitle = rows[1] && rows[1].filter(Boolean).length === 1 ? (rows[1][0] || '').trim() : '';
  let suggestedLine = '';
  if (subtitle) {
    const clause = subtitle.split(/[.;]/)[0].trim();
    if (/line|induction|maintenance|adjuvant|neoadjuvant|\d\s*L/i.test(clause)) {
      suggestedLine = clause;
    }
  }
  if (!suggestedLine && title) {
    const before = title.split(/\s*[—–\-]\s*/)[0].trim();
    const m = before.match(/^(\d+\s*L\s*\+?(?:\s+\S+){0,6})/i);
    suggestedLine = (m ? m[1] : before).replace(/\s+/g, ' ').trim();
  }
  let suggestedSlug = '';
  if (/urothelial|m\s*uc/i.test(title)) {
    if (/^2\s*L|\b2L\b/i.test(title)) suggestedSlug = '2l-uc';
    else if (/^1\s*L|\b1L\b/i.test(title)) suggestedSlug = '1l-uc';
  } else if (suggestedLine) {
    suggestedSlug = suggestedLine
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 40);
  }
  return { title, suggestedLine, suggestedSlug };
}

const ManageEmergingLandscapes: React.FC = () => {
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [indications, setIndications] = useState<IndicationOption[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [visibilityBusyId, setVisibilityBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [viewUrl, setViewUrl] = useState<string | null>(null);

  const [landscapeFile, setLandscapeFile] = useState<File | null>(null);
  const [legendFile, setLegendFile] = useState<File | null>(null);
  const [slug, setSlug] = useState('');
  const [diseaseArea, setDiseaseArea] = useState('');
  const [lineOfTherapy, setLineOfTherapy] = useState('');
  const [versionLabel, setVersionLabel] = useState('');
  const [indicationName, setIndicationName] = useState('');

  const loadList = async () => {
    try {
      setLoadingList(true);
      const [listRes, indRes] = await Promise.all([
        apiService.fetchEmergingLandscapes({ includeAll: true }),
        apiService.fetchEmergingIndications(),
      ]);
      if (listRes?.success) setItems(listRes.data || []);
      setIndications(
        (indRes || []).map((ind: { id: string; name: string }) => ({
          id: String(ind.id),
          name: ind.name,
        })),
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load landscapes');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  const slugOptions = useMemo(() => uniqueSorted(items.map((i) => i.slug)), [items]);
  const diseaseAreaOptions = useMemo(
    () => uniqueSorted(items.map((i) => i.disease_area)),
    [items],
  );
  const lineOptions = useMemo(
    () => uniqueSorted(items.map((i) => i.line_of_therapy)),
    [items],
  );

  const matchingLive = useMemo(() => {
    const key = slug.trim().toLowerCase();
    if (!key) return null;
    return (
      items.find((i) => i.is_published && i.slug.toLowerCase() === key) ||
      items.find((i) => i.slug.toLowerCase() === key) ||
      null
    );
  }, [items, slug]);

  const applyExactKeys = (item: CatalogItem, { scroll = false }: { scroll?: boolean } = {}) => {
    setSlug(item.slug || '');
    setDiseaseArea(item.disease_area || '');
    setLineOfTherapy(item.line_of_therapy || '');
    setIndicationName(item.indication_name || '');
    setVersionLabel('');
    setError(null);
    setSuccess(
      `Prefill ready to overwrite “${item.slug}”` +
        `${item.indication_name ? ` · indication “${item.indication_name}”` : ''}` +
        ` — next upload becomes v${(item.version_number || 0) + 1}.`,
    );
    setViewUrl(null);
    if (scroll) window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // When slug first matches an existing landscape, fill blank disease / line / indication.
  useEffect(() => {
    if (!matchingLive) return;
    setDiseaseArea((prev) => prev.trim() || matchingLive.disease_area || '');
    setLineOfTherapy((prev) => prev.trim() || matchingLive.line_of_therapy || '');
    setIndicationName((prev) => prev.trim() || matchingLive.indication_name || '');
  }, [matchingLive]);

  const prefillFromItem = (item: CatalogItem) => {
    applyExactKeys(item, { scroll: true });
  };

  const handleVisibility = async (item: CatalogItem, action: 'archive' | 'restore') => {
    const verb = action === 'archive' ? 'hide' : 'restore';
    const confirmed = window.confirm(
      action === 'archive'
        ? `Hide “${item.title}” (v${item.version_number}) from clients? You can restore it later.`
        : `Publish “${item.title}” (v${item.version_number}) as the live version for slug “${item.slug}”?`,
    );
    if (!confirmed) return;
    setError(null);
    setSuccess(null);
    setViewUrl(null);
    try {
      setVisibilityBusyId(item.id);
      const response = await apiService.setEmergingLandscapeVisibility(item.id, action);
      if (!response?.success) {
        throw new Error(response?.error || `Failed to ${verb} landscape`);
      }
      setSuccess(
        action === 'archive'
          ? `Hidden “${item.slug}” v${item.version_number} from clients.`
          : `Published “${item.slug}” v${item.version_number} — clients see this version.`,
      );
      await loadList();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : err instanceof Error
            ? err.message
            : `Failed to ${verb} landscape`;
      setError(message || `Failed to ${verb} landscape`);
    } finally {
      setVisibilityBusyId(null);
    }
  };

  const handleUpload = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    setViewUrl(null);
    if (!landscapeFile) {
      setError('Please choose a landscape CSV file.');
      return;
    }
    if (!slug.trim()) {
      setError('Slug is required (e.g. 1l-pdac).');
      return;
    }
    try {
      setUploading(true);
      const response = await apiService.uploadEmergingLandscape({
        landscapeCsv: landscapeFile,
        legendCsv: legendFile || undefined,
        slug: slug.trim(),
        disease_area: diseaseArea.trim() || undefined,
        line_of_therapy: lineOfTherapy.trim() || undefined,
        version_label: versionLabel.trim() || undefined,
        indication_name: indicationName.trim() || undefined,
      });
      if (!response?.success) {
        throw new Error(response?.error || 'Upload failed');
      }
      const stats = response.stats || {};
      const parts = [
        `Published ${stats.slug} as v${stats.version_number}` +
          `${stats.version_label ? ` (${stats.version_label})` : ''}` +
          ` — clients now see this upload.`,
        `${stats.columns} columns, ${stats.rows} rows, ${stats.ncts} NCTs (${stats.ncts_linked_to_trials} linked` +
          `${stats.ncts_created ? `, ${stats.ncts_created} created in 1emerging_trials` : ''}` +
          `${
            stats.ncts_enriched_from_ctgov
              ? `; ${stats.ncts_enriched_from_ctgov} enriched from ClinicalTrials.gov`
              : ''
          }` +
          `${stats.ncts_refreshed ? `; ${stats.ncts_refreshed} refreshed` : ''}` +
          `${stats.events_seeded ? `; ${stats.events_seeded} tracker alerts seeded` : ''}).`,
      ];
      if (stats.trials_indication_updated) {
        parts.push(`Updated indication_codes on ${stats.trials_indication_updated} existing trials.`);
      }
      if (Array.isArray(stats.ncts_fetch_failed) && stats.ncts_fetch_failed.length) {
        parts.push(`CT.gov fetch failed for ${stats.ncts_fetch_failed.length} NCT(s).`);
      }
      if (stats.reused_existing_keys) {
        parts.push(
          `Reused exact keys: disease_area “${stats.disease_area || '—'}”, line “${
            stats.line_of_therapy || '—'
          }”.`,
        );
      }
      if (stats.indication_created) {
        parts.push(`Created indication “${stats.indication_name}” in emerging_disease_indications.`);
      } else if (stats.indication_name) {
        parts.push(`Linked indication “${stats.indication_name}”.`);
      }
      setSuccess(parts.join(' '));
      setViewUrl(
        `/emerging-landscapes/${encodeURIComponent(
          (stats.disease_area || diseaseArea || stats.slug || 'landscape')
            .toString()
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '-'),
        )}`,
      );
      if (stats.disease_area) setDiseaseArea(String(stats.disease_area));
      if (stats.line_of_therapy) setLineOfTherapy(String(stats.line_of_therapy));
      if (stats.indication_name) setIndicationName(String(stats.indication_name));
      await loadList();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : err instanceof Error
            ? err.message
            : 'Upload failed';
      setError(message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card px-5 py-4">
        <h1 className="text-xl font-bold text-slate-900">Emerging Landscapes</h1>
        <p className="text-sm text-slate-500 mt-1">
          Admin-only: run the HCC/DLBCL landscape Cloud Run pipeline, then manage CSV matrix
          uploads for the client Emerging view.{' '}
          <Link to="/emerging-landscapes" className="font-semibold text-indigo-600 hover:underline">
            Open client landscapes
          </Link>
          .
        </p>
      </div>

      <LandscapePipelinePanel />

      <form
        onSubmit={handleUpload}
        className="bg-white rounded-2xl border border-gray-100 shadow-card px-5 py-4 space-y-4"
      >
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Upload CSV</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="space-y-1 text-sm font-medium text-slate-700">
            Landscape CSV
            <input
              type="file"
              accept=".csv,text/csv"
              required
              onChange={async (e) => {
                const file = e.target.files?.[0] || null;
                setLandscapeFile(file);
                if (!file) return;
                try {
                  const hints = await peekLandscapeCsvHints(file);
                  if (hints.suggestedLine) {
                    setLineOfTherapy((prev) => prev.trim() || hints.suggestedLine || '');
                  }
                  if (hints.suggestedSlug) {
                    setSlug((prev) => prev.trim() || hints.suggestedSlug || '');
                  }
                  if (hints.title && /urothelial|muc|bladder/i.test(hints.title)) {
                    setDiseaseArea((prev) => prev.trim() || 'Urothelial Carcinoma');
                  }
                } catch {
                  // ignore peek errors — upload still works
                }
              }}
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-indigo-700"
            />
          </label>
          <label className="space-y-1 text-sm font-medium text-slate-700">
            Legend & Notes CSV (optional)
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setLegendFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-50 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-slate-700"
            />
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="space-y-1 text-sm font-medium text-slate-700">
            Slug
            <input
              list="landscape-slug-options"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="1l-pdac"
              required
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
            <datalist id="landscape-slug-options">
              {slugOptions.map((value) => (
                <option key={value} value={value} />
              ))}
            </datalist>
          </label>
          <label className="space-y-1 text-sm font-medium text-slate-700">
            Disease area
            <input
              list="landscape-disease-area-options"
              value={diseaseArea}
              onChange={(e) => setDiseaseArea(e.target.value)}
              placeholder="PDAC"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
            <datalist id="landscape-disease-area-options">
              {diseaseAreaOptions.map((value) => (
                <option key={value} value={value} />
              ))}
            </datalist>
          </label>
          <label className="space-y-1 text-sm font-medium text-slate-700">
            Line of therapy
            <input
              list="landscape-line-options"
              value={lineOfTherapy}
              onChange={(e) => setLineOfTherapy(e.target.value)}
              placeholder="First-line (induction + switch-maintenance)"
              maxLength={160}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
            <datalist id="landscape-line-options">
              {lineOptions.map((value) => (
                <option key={value} value={value} />
              ))}
            </datalist>
            <span className="text-[11px] font-normal text-slate-400">
              Shown exactly as entered on the client line toggle (auto-filled from the CSV when blank).
            </span>
          </label>
          <label className="space-y-1 text-sm font-medium text-slate-700">
            Version label (optional display name)
            <input
              value={versionLabel}
              onChange={(e) => setVersionLabel(e.target.value)}
              placeholder="July 2026"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
            <span className="text-[11px] font-normal text-slate-400">
              Not used for recency — uploaded timestamp / auto version number decide which is latest.
            </span>
          </label>
          <label className="space-y-1 text-sm font-medium text-slate-700 md:col-span-2">
            Indication name
            <input
              list="landscape-indication-options"
              value={indicationName}
              onChange={(e) => setIndicationName(e.target.value)}
              placeholder="1L PDAC — created if missing"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
            <datalist id="landscape-indication-options">
              {indications.map((ind) => (
                <option key={ind.id} value={ind.name} />
              ))}
            </datalist>
            <span className="text-[11px] font-normal text-slate-400">
              Exact match links existing; otherwise a new row is created in emerging_disease_indications.
            </span>
          </label>
        </div>

        {matchingLive && (
          <div className="text-sm text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 flex flex-wrap items-center justify-between gap-2">
            <span>
              Slug <span className="font-semibold">{matchingLive.slug}</span> already exists
              {matchingLive.is_published ? ' (live)' : ' (archived)'}. Disease area, line, and
              indication are filled from that version — upload will publish as{' '}
              <span className="font-semibold">v{(matchingLive.version_number || 0) + 1}</span>.
              {matchingLive.indication_name ? (
                <> Indication: “{matchingLive.indication_name}”.</>
              ) : (
                <> No indication linked yet — enter one above to create/link it.</>
              )}
            </span>
            <button
              type="button"
              onClick={() => applyExactKeys(matchingLive)}
              className="text-xs font-semibold text-amber-900 underline whitespace-nowrap"
            >
              Refresh exact keys
            </button>
          </div>
        )}

        {error && <p className="text-sm text-rose-600 font-medium">{error}</p>}
        {success && (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
            {success}
            {viewUrl && (
              <>
                {' '}
                <Link to={viewUrl} className="font-semibold underline">
                  Open landscape
                </Link>
              </>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={uploading}
          className="inline-flex items-center rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
        >
          {uploading ? 'Uploading…' : 'Upload & publish as latest'}
        </button>
      </form>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-card px-5 py-4">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-1">All uploads</h2>
        <p className="text-xs text-slate-500 mb-3">
          Only the <span className="font-semibold text-emerald-700">Live</span> row is shown to clients.
          Use <span className="font-semibold">Hide</span> to archive a live landscape, or{' '}
          <span className="font-semibold">Publish</span> to restore a hidden/older version.
          Click <span className="font-semibold">Overwrite</span> to copy exact slug / disease area /
          line / indication into the form.
        </p>
        {loadingList ? (
          <div className="py-6 flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No landscapes yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((item) => (
              <li
                key={`${item.slug}-v${item.version_number}-${item.id}`}
                className={`py-2.5 flex items-center justify-between gap-3 ${
                  item.is_published ? '' : 'opacity-60'
                }`}
              >
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{item.title}</span>
                    {item.is_published ? (
                      <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                        Live
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                        Archived
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {item.slug} · v{item.version_number ?? '?'}
                    {item.version_label ? ` · label “${item.version_label}”` : ''}
                    {` · uploaded ${formatUploadedAt(item.created_at)}`}
                    {[item.disease_area, item.line_of_therapy, item.indication_name]
                      .filter(Boolean)
                      .map((part) => ` · ${part}`)
                      .join('')}
                    {item.column_count != null ? ` · ${item.column_count} columns` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-3 whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => prefillFromItem(item)}
                    className="text-xs font-semibold text-slate-600 hover:text-slate-900"
                  >
                    Overwrite
                  </button>
                  {item.is_published ? (
                    <button
                      type="button"
                      disabled={visibilityBusyId === item.id}
                      onClick={() => handleVisibility(item, 'archive')}
                      className="text-xs font-semibold text-rose-600 hover:text-rose-800 disabled:opacity-50"
                    >
                      {visibilityBusyId === item.id ? 'Hiding…' : 'Hide'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={visibilityBusyId === item.id}
                      onClick={() => handleVisibility(item, 'restore')}
                      className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 disabled:opacity-50"
                    >
                      {visibilityBusyId === item.id ? 'Publishing…' : 'Publish'}
                    </button>
                  )}
                  {item.is_published && (
                    <Link
                      to={`/emerging-landscapes/${encodeURIComponent(
                        (item.disease_area || item.slug).trim().toLowerCase().replace(/\s+/g, '-'),
                      )}`}
                      className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                    >
                      View →
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ManageEmergingLandscapes;
